import React, { useState, useEffect } from 'react';
import { Check, X, Loader2, CalendarX } from 'lucide-react';
import { supabaseUrl } from '../../lib/supabase/client';

/**
 * Öffentliche Bestätigungsseite für Termin-Einladungen (/termin-antwort?id&sig&antwort).
 * Ohne Login erreichbar — die Zusagen/Absagen-Links in der Einladungs-Mail zeigen
 * hierher; Verifikation läuft über die HMAC-Signatur in der Edge Function
 * termin-antwort. Der Status wird erst per Button-Klick gesetzt (nie beim Laden),
 * damit Link-Prefetcher von Mail-Programmen keine Geister-Zusagen auslösen.
 */

const API = `${supabaseUrl}/functions/v1/termin-antwort`;

interface TerminInfo { titel: string; zeit: string; teilnehmerAnzahl: number }

const TerminAntwortSeite: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const id = params.get('id') || '';
  const sig = params.get('sig') || '';
  const vorgewaehlt = params.get('antwort') === 'absagen' ? 'absagen' : 'zusagen';

  const [laden, setLaden] = useState(true);
  const [termin, setTermin] = useState<TerminInfo | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [sende, setSende] = useState(false);
  const [ergebnis, setErgebnis] = useState<{ name: string; zugesagt: boolean } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}?id=${encodeURIComponent(id)}&sig=${encodeURIComponent(sig)}`);
        const d = await res.json();
        if (d.ok) setTermin(d); else setFehler(d.fehler || 'Dieser Link ist ungültig.');
      } catch {
        setFehler('Verbindungsfehler — bitte laden Sie die Seite neu.');
      } finally {
        setLaden(false);
      }
    })();
  }, [id, sig]);

  const antworten = async (antwort: 'zusagen' | 'absagen') => {
    if (termin && termin.teilnehmerAnzahl > 1 && !email.trim()) {
      setFehler('Bitte geben Sie Ihre E-Mail-Adresse an.');
      return;
    }
    setSende(true);
    setFehler(null);
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, sig, antwort, email: email.trim() || undefined }),
      });
      const d = await res.json();
      if (d.ok) setErgebnis({ name: d.name, zugesagt: d.zugesagt });
      else setFehler(d.fehler || 'Das hat leider nicht geklappt.');
    } catch {
      setFehler('Verbindungsfehler — bitte erneut versuchen.');
    } finally {
      setSende(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1a1d24] p-6">
      <div className="w-full max-w-md bg-white/[0.06] border border-white/10 rounded-[2rem] backdrop-blur-xl p-10 text-center">
        <p className="text-[10px] font-black text-[#c4a77d] uppercase tracking-[0.3em] mb-8">
          Immobilienmanagement Schwede
        </p>

        {laden && (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 size={28} className="text-[#c4a77d] animate-spin" />
            <p className="text-xs font-semibold text-white/40 uppercase tracking-widest">Termin wird geladen…</p>
          </div>
        )}

        {!laden && !termin && (
          <div className="py-4">
            <CalendarX size={36} className="text-white/30 mx-auto mb-5" />
            <h1 className="text-lg font-black text-white mb-2">Link ungültig</h1>
            <p className="text-sm text-white/60 leading-relaxed">{fehler}</p>
          </div>
        )}

        {!laden && termin && ergebnis && (
          <div className="py-4">
            <div className={`w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center ${ergebnis.zugesagt ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
              {ergebnis.zugesagt ? <Check size={30} /> : <X size={30} />}
            </div>
            <h1 className="text-lg font-black text-white mb-3">
              {ergebnis.zugesagt ? 'Zusage eingetragen' : 'Absage eingetragen'}
            </h1>
            <p className="text-sm text-white/70 leading-relaxed">
              {ergebnis.zugesagt
                ? <>Vielen Dank, {ergebnis.name} — wir freuen uns auf Sie!</>
                : <>Schade, {ergebnis.name}. Wir haben Ihre Absage vermerkt.</>}
            </p>
          </div>
        )}

        {!laden && termin && !ergebnis && (
          <>
            <h1 className="text-xl font-black text-white mb-2">{termin.titel}</h1>
            <p className="text-sm text-white/50 mb-8">{termin.zeit}</p>

            {termin.teilnehmerAnzahl > 1 && (
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Ihre E-Mail-Adresse"
                autoComplete="email"
                className="w-full px-4 py-3.5 mb-5 rounded-xl bg-white/5 border border-white/15 text-sm font-semibold text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40 focus:border-[#c4a77d]/40 transition-all"
              />
            )}

            <div className="flex justify-center gap-3">
              <button
                onClick={() => antworten('zusagen')}
                disabled={sende}
                className={`flex items-center gap-2 px-7 py-3.5 rounded-xl text-[11px] font-black uppercase tracking-widest text-white bg-emerald-600 hover:bg-emerald-500 transition-all active:scale-95 disabled:opacity-50 ${vorgewaehlt === 'zusagen' ? 'ring-2 ring-white/40' : ''}`}
              >
                {sende ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Zusagen
              </button>
              <button
                onClick={() => antworten('absagen')}
                disabled={sende}
                className={`flex items-center gap-2 px-7 py-3.5 rounded-xl text-[11px] font-black uppercase tracking-widest text-white bg-red-700 hover:bg-red-600 transition-all active:scale-95 disabled:opacity-50 ${vorgewaehlt === 'absagen' ? 'ring-2 ring-white/40' : ''}`}
              >
                {sende ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />} Absagen
              </button>
            </div>

            {fehler && <p className="mt-5 text-sm font-semibold text-red-400">{fehler}</p>}
          </>
        )}
      </div>
    </div>
  );
};

export default TerminAntwortSeite;
